//External
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { concat, Observable, of, Subject } from 'rxjs';
import { map, last, catchError, takeUntil } from 'rxjs/operators';

//Internal
import { PrivacyActionsService } from '../../../pages/privacy/privacyActions.service';
import { ModalName, ModalRoutelessService } from '../../components/ui/ui-modal-routeless/modal.routeless.service';
import { PrivacyValuesService } from '../../values/privacy.values.service';
import { ModalSize } from '../global/modal/Modal.model';
import { PrivacyService } from '../process/privacy/privacy.service';
import { ModalAction } from './helpers/models/special.cases.model';
import { NavigationService } from '../../navigation/navigation.service';
import { PrivacyHelperService } from './helpers/privacy.helper.service';
import { SubscriptionsService } from '../process/subscriptions/subscriptions.service';

@Injectable({
    providedIn: 'root'
})

export class PrivacyResolver {

    private readonly onDestroy$: Subject<void> = new Subject<void>();

    constructor(
        private readonly modalRoutelessService: ModalRoutelessService,
        private readonly navigationService: NavigationService,
        private readonly privacyActionsService: PrivacyActionsService,
        private readonly privacyHelperService: PrivacyHelperService,
        private readonly privacyService: PrivacyService,
        private readonly privacyValuesService: PrivacyValuesService,
        private readonly subscriptionsService: SubscriptionsService
    ) { }

    /**
     * Se obtin parametrii de configurare si identitatea curenta
     * @param {ActivatedRouteSnapshot} current route info
     * @param {RouterStateSnapshot} current state of the router
     * @returns {Observable<boolean|UrlTree>} true if route is allowed or a UrlTree that has the new route to which will be redirected
     */
    resolve(_route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<any> {
        const urlProperties = this.navigationService.processUrl(_route, _state);
        const privacyPage = '/'.concat(urlProperties.pathPieces[1]);
        const actionResponses = [];

        if (this.privacyService.getMainReqHasError()) {
            return of(true);
        }

        return concat(
            this.checkOnBoardingModal(),
            this.listConfigSettings(),
            this.checkAboutModal(privacyPage)
        ).pipe(
            map(response => {
                if (response) {
                    actionResponses.push(response);
                }
            }),
            last(),
            map(() => {
                if (actionResponses.length) {
                    if (actionResponses[0].isAboutModal) {
                        this.modalRoutelessService.open(actionResponses[0].name, actionResponses[0].containerOptions, actionResponses[0].contentOptions);
                    } else {
                        this.modalRoutelessService.open(actionResponses[0].name, actionResponses[0].containerOptions, actionResponses[0].contentOptions, false, { automatic: true });
                    }
                } else {
                    return of(true);
                }
            }),
            catchError( (err) => {
                console.log("Error: ", err);
                return of(true);
            })
        );
    }

    /**
     * Se iau restul de date necesare pt deschiderea privacy-ului
     * @returns {Observable<boolean>} ne intereseaza doar sa se faca req de date nu si rezultatul
     */
    listConfigSettings(): Observable<boolean> {
        return new Observable(subscriber => {
            if (!this.privacyService.getProvidedIdentity() || this.privacyService.getParkedIdentityStatus()) {
                subscriber.next(false);
                subscriber.complete();
                return;
            }

            this.privacyActionsService.listConfigParams()
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
                next: () => {
                    subscriber.next(false);
                    subscriber.complete();
                },
                error: () => {
                    subscriber.next(false);
                    subscriber.complete();
                }
            });
        });
    }

    /**
     * Se verifica daca avem identitate si daca sunt toate metadatele introduse pt a decide in ce stare se deschide modala de onboarding
     * @returns {Observable<ModalAction | false>} false daca nu luam nici o actiune sau obiectul modalei care va fi deschisa
     */
    private checkOnBoardingModal(): Observable<ModalAction | boolean> {
        return new Observable(subscriber => {
            //a expirat subscriptia
            if (!this.subscriptionsService.hasDataPrivacy() || this.subscriptionsService.hasDataPrivacyExpired()) {
                subscriber.next(false);
                subscriber.complete();
                return;
            }
            //daca nu avem identitatea, trebuie deschisa modala de onboarding
            if (!this.privacyService.getProvidedIdentity()) {
                const modalObj = this.privacyHelperService.openOnBoardingFromBegining();
                subscriber.next(modalObj);
                subscriber.complete();
                return;
            } else if (this.privacyService.getParkedIdentityStatus()) {
                subscriber.next(false);
                subscriber.complete();
                return;
            }

            //daca nu este init_done si nici metadate, inseamna ca userul a facut onboardingul dar inca nu am primit datele de la lid
            const metadataTypeNeededForOnboarding = this.privacyService.getNextMetatdataTypeForFinishingOnboarding();
            if (!this.privacyService.isProvidedIdentityInitDone() && !metadataTypeNeededForOnboarding) {
                this.privacyService.getInfoAfterOnboarding()
                .pipe(takeUntil(this.onDestroy$))
                .subscribe({
                    next:(res: boolean) => {
                        subscriber.next(this.privacyHelperService.openOnBoardingWaitingStatus());
                        subscriber.complete();
                    },
                    error: (err) => {
                        if (!this.privacyService.checkParkedIdentity(err, true)) {
                            subscriber.next(this.privacyHelperService.openOnBoardingWaitingStatus());
                            subscriber.complete();
                        } else {
                            subscriber.next(false);
                            subscriber.complete();
                        }
                    }
                });
            } else if (!this.privacyService.isProvidedIdentityInitDone()) {
                const modalObj = this.privacyHelperService.finishOnBoardingDataValidations();
                subscriber.next(modalObj);
                subscriber.complete();
            } else {
                subscriber.next(false);
                subscriber.complete();
            }
        });
    }

    /**
     * Metoda ce deschide modala de about pe fiecare tab din privacy
     * @param {string} pagina curenta din privacy
     * @returns {Observable<ModalAction | false>} false daca nu luam nici o actiune sau obiectul modalei care va fi deschisa
     */
    private checkAboutModal(privacyPage: string): Observable<ModalAction | false> {
        return new Observable(subscriber => {
            const aboutSection = this.privacyValuesService.pageToAboutModalSectionCorrespondence[privacyPage];
            const identityIsNotOk = !this.privacyService.getProvidedIdentity() || this.privacyService.getParkedIdentityStatus();
            const doNotShowModal = !aboutSection || !this.privacyActionsService.getConfigParam(this.privacyValuesService.aboutModalsInfo[aboutSection]);

            if (identityIsNotOk || doNotShowModal) {
                subscriber.next(false);
                subscriber.complete();
                return;
            }

            const containerOptions = {
                name: ModalName.aboutProtectionModal,
                size: ModalSize.SM,
                dismissable: true
            };
            const contentOptions = {
                section: aboutSection
            };
            const response = {
                name: ModalName.aboutProtectionModal,
                isAboutModal: true,
                containerOptions,
                contentOptions
            };

            subscriber.next(response);
            subscriber.complete();
        });
    }

}
